<template>
  <section id="distribution" class="dist-section" aria-labelledby="dist-title">
    <div class="dist-container">
      <div class="dist-text" data-reveal="left">
        <span class="section-label coral">{{ $t('landing.dist_label') }}</span>
        <h2 id="dist-title" class="section-title">
          {{ $t('landing.dist_title_pre') }}
          <span class="gradient-text-coral">{{ $t('landing.dist_title_highlight') }}</span>
        </h2>
        <ul class="dist-bullets">
          <li v-for="i in 5" :key="i" class="bullet-item">
            <span class="bullet-check">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </span>
            {{ $t(`landing.dist_bullet_${i}`) }}
          </li>
        </ul>
        <NuxtLink :to="localePath('/auth') + '?tab=signup'" class="btn-cyber-primary btn-dist">
          {{ $t('landing.dist_cta') }}
        </NuxtLink>
      </div>

      <div class="dist-visual" data-reveal="right">
        <!-- CSS-only release card mockup -->
        <div class="mockup-card">
          <div class="mockup-glow"></div>
          <div class="mockup-artwork">
            <div class="artwork-gradient"></div>
            <div class="artwork-play">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <polygon points="5 3 19 12 5 21 5 3" />
              </svg>
            </div>
          </div>
          <div class="mockup-info">
            <div class="mockup-title-bar"></div>
            <div class="mockup-artist-bar"></div>
          </div>
          <div class="mockup-tracks">
            <div v-for="i in 3" :key="i" class="mockup-track">
              <span class="track-num">{{ i }}</span>
              <span class="track-bar"></span>
              <span class="track-duration">{{ ['3:24', '4:01', '2:57'][i-1] }}</span>
            </div>
          </div>
          <div class="mockup-platforms">
            <div v-for="i in 6" :key="i" class="platform-dot" :style="{ animationDelay: `${i * 0.3}s` }">
              <span class="dot-pulse"></span>
            </div>
          </div>
          <!-- Connection lines -->
          <div class="connection-line line-1"></div>
          <div class="connection-line line-2"></div>
          <div class="connection-line line-3"></div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
const localePath = useLocalePath()
</script>

<style scoped>
/* Mobile-first. Ladder: 480 / 640 / 768 / 1024 / 1280 (variables.css). */
.dist-section {
  position: relative;
  padding: var(--landing-section-pad) 0;
  overflow: hidden;
}

/* Giant blurred accent orb — desktop decoration only; a 600px blur(200px)
   paint is far too expensive for phones. */
@media (min-width: 768px) {
  .dist-section::before {
    content: '';
    position: absolute;
    top: 50%;
    right: -200px;
    width: 600px;
    height: 600px;
    border-radius: 50%;
    background: var(--secondary);
    filter: blur(200px);
    opacity: 0.06;
    transform: translateY(-50%);
    pointer-events: none;
  }
}

.dist-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--space-6);
  display: grid;
  grid-template-columns: 1fr;
  gap: 48px;
  align-items: center;
}

@media (min-width: 768px) {
  .dist-container {
    grid-template-columns: 1fr 1fr;
  }
}

@media (min-width: 1024px) {
  .dist-container {
    gap: 80px;
  }
}

/* Text side */
.dist-text {
  max-width: 500px;
}

.section-label {
  display: inline-block;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  margin-bottom: var(--space-4);
}

.section-label.coral {
  color: var(--secondary);
}

.section-title {
  font-family: var(--font-heading);
  font-size: var(--landing-title-size);
  font-weight: 800;
  line-height: 1.1;
  color: #fff;
  margin-bottom: var(--space-8);
  text-wrap: balance;
}

.gradient-text-coral {
  background: linear-gradient(135deg, var(--secondary), var(--primary));
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.dist-bullets {
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: var(--space-8);
}

.bullet-item {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 1rem;
  color: var(--text-muted);
}

.bullet-check {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(255, 107, 107, 0.15);
  color: var(--secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.btn-dist {
  display: inline-flex;
}

/* Visual side — mockup card */
.dist-visual {
  display: flex;
  justify-content: center;
  position: relative;
}

.mockup-card {
  position: relative;
  width: min(320px, 100%);
  background: var(--surface-color);
  border: 1px solid var(--border);
  border-radius: var(--radius-xl);
  padding: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
}

.mockup-glow {
  position: absolute;
  top: -40px;
  left: -40px;
  right: -40px;
  bottom: -40px;
  background: radial-gradient(circle, rgba(255, 107, 107, 0.08) 0%, transparent 70%);
  pointer-events: none;
  z-index: -1;
}

.mockup-artwork {
  position: relative;
  width: 100%;
  aspect-ratio: 1;
  border-radius: var(--radius-md);
  overflow: hidden;
  margin-bottom: 16px;
}

.artwork-gradient {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(255, 107, 107, 0.6), rgba(0, 242, 255, 0.4), rgba(255, 107, 107, 0.3));
}

/* continuous mockup animations are desktop-only decoration */
@media (min-width: 768px) {
  .artwork-gradient {
    animation: artworkShift 8s ease-in-out infinite;
  }
}

@keyframes artworkShift {
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
}

.artwork-play {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(8px);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  padding-left: 2px;
}

.mockup-info {
  margin-bottom: 16px;
}

.mockup-title-bar {
  height: 12px;
  width: 70%;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  margin-bottom: 8px;
}

.mockup-artist-bar {
  height: 10px;
  width: 40%;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 5px;
}

.mockup-tracks {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 20px;
}

.mockup-track {
  display: flex;
  align-items: center;
  gap: 10px;
}

.track-num {
  font-size: 0.75rem;
  color: var(--text-dim);
  width: 16px;
  text-align: center;
}

.track-bar {
  flex: 1;
  height: 8px;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 4px;
}

.track-duration {
  font-size: 0.7rem;
  color: var(--text-dim);
}

.mockup-platforms {
  display: flex;
  justify-content: center;
  gap: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--border);
}

.platform-dot {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 107, 107, 0.15);
  border: 1px solid rgba(255, 107, 107, 0.3);
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dot-pulse {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--secondary);
  opacity: 0.8;
}

@media (min-width: 768px) {
  .dot-pulse {
    animation: dotPulse 2s ease-in-out infinite;
  }
}

@keyframes dotPulse {
  0%, 100% { opacity: 0.4; transform: scale(0.8); }
  50% { opacity: 1; transform: scale(1.2); }
}

/* Connection lines (decorative) */
.connection-line {
  position: absolute;
  width: 1px;
  background: linear-gradient(to bottom, var(--secondary), transparent);
  opacity: 0.2;
}

.line-1 {
  right: -30px;
  top: 40%;
  height: 60px;
  transform: rotate(45deg);
}

.line-2 {
  right: -50px;
  top: 50%;
  height: 80px;
  transform: rotate(30deg);
}

.line-3 {
  right: -20px;
  top: 65%;
  height: 50px;
  transform: rotate(55deg);
}

/* On phones the visual leads, the pitch follows (base = mobile). */
.dist-visual {
  order: -1;
}

@media (min-width: 768px) {
  .dist-visual {
    order: 0;
  }
}
</style>
